import { InternalBanks } from './bankAccount'
import { ClientInfoStatus } from './client'

/** New FBO (collections) accounts are only opened at IDB. Existing Webster ones still list. */
export const FBO_BANK = InternalBanks.IDB

export enum FboBankAccountStatus {
  Active = 'Active',
  /** `bank_account_info.closed_at` is set. Kept, never deleted, so past wires still resolve. */
  Closed = 'Closed',
}

/**
 * An internal `bank_account_info` row: a Dwight FBO account. Wires identify it by last four;
 * `clientName` is null until the account is given to a client.
 */
export interface IFboBankAccount {
  id: string
  bankName: string
  accountNumber: string
  lastFour: string
  clientName: string | null
  /** The client's id, status and lockbox; null while the account is unassigned. */
  clientId: string | null
  clientStatus: ClientInfoStatus | null
  lockbox: string | null
  collectionsAccountNumber: number | null
  status: FboBankAccountStatus
  closedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface IFboBankAccountCreatePayload {
  accountNumber: string
  collectionsAccountNumber?: number | null
  clientName?: string
}

export interface IFboBankAccountUpdatePayload {
  accountNumber?: string
  /** Null clears it. Omitted leaves it as it is. */
  collectionsAccountNumber?: number | null
  /** Sets the assigned client's lockbox; null clears it. Omitted leaves it as it is. */
  lockbox?: number | null
  /** Assigns or reassigns the account. Omitted leaves the assignment as it is. */
  clientName?: string
  isClosed?: boolean
}

export interface IFboBankAccountsSummary {
  bankAccounts: Array<{ bankName: string; count: number }>
  unassignedCount: number
  lockboxes: Array<{ lockbox: string; count: number }>
}

export const FBO_BANK_ACCOUNT_NUMBER_REGEX = /^\d{4,17}$/

export const COLLECTIONS_ACCOUNT_NUMBER_MAX = 9999
export const COLLECTIONS_ACCOUNT_NUMBER_REGEX = /^[1-9]\d{0,3}$/

/** The account modal's values: numbers stay strings until they're sent. */
export interface IFboBankAccountFormValues {
  accountNumber: string
  collectionsAccountNumber?: string
  clientName?: string
  lockbox?: string
}

export const FBO_BANK_ACCOUNT_IMPORT_MAX_ROWS = 200

export enum FboBankAccountImportAction {
  Create = 'Create',
  /** Gives an existing unassigned account to the client in the row. Reassigning is done one at a time. */
  Assign = 'Assign',
  /** Adds the collections # to an existing account that has none. */
  Number = 'Number',
  Unchanged = 'Unchanged',
  Error = 'Error',
}

export interface IFboBankAccountImportRow {
  accountNumber: string
  /** Kept as pasted so the plan can report a malformed value per row. */
  collectionsAccountNumber?: string
  clientName?: string
}

export interface IFboBankAccountImportPayload {
  rows: IFboBankAccountImportRow[]
  /** Plans the import without writing, so the rows can be previewed. */
  dryRun?: boolean
}

export interface IFboBankAccountImportResultRow {
  rowNumber: number
  lastFour: string
  collectionsAccountNumber: number | null
  clientName: string | null
  action: FboBankAccountImportAction
  error?: string
}

export interface IFboBankAccountImportResult {
  rows: IFboBankAccountImportResultRow[]
  hasErrors: boolean
}
